import { computed, type MaybeRefOrGetter, toValue } from 'vue'
import { useQuery } from '@tanstack/vue-query'
import { http, isApiError, unwrap } from './client'
import { workspacePath } from './workspaces'
import type { ApiEnvelope } from '@/types/api'
import type { MetricReadiness, PipelineHealth } from '@/types/intelligence'

/** P2 §4's review horizons; CUSTOM takes a first and last day. */
export type PeriodKind = 'TODAY' | 'LAST_7_DAYS' | 'LAST_30_DAYS' | 'CUSTOM'

export interface PeriodSelection {
  period: PeriodKind
  /** `YYYY-MM-DD`, CUSTOM only. */
  from?: string
  to?: string
}

export type OperationsMetricId =
  | 'METRIC-OP-APPOINTMENTS'
  | 'METRIC-OP-VISITS-COMPLETED'
  | 'METRIC-OP-PATIENTS-SEEN'
  | 'METRIC-OP-WAITING-TIME'
  | 'METRIC-OP-ACTIVE-MEMBERS'

export type BlockedReason = 'NOT_CONNECTED' | 'UNREACHABLE' | 'NOT_LOADED'

/** What limits a value, for the screen to put into words. */
export type MetricNote =
  | { code: BlockedReason }
  | { code: 'TOO_FEW'; measured: number; minimum: number }
  | { code: 'WAITING_ROOM_VISITS_ONLY'; measured: number; of: number }
  /** No longer sent (DECISIONS.md #37); opportunities stored before it still carry it. */
  | { code: 'LATEST_ASSIGNMENT' }
  /** The period starts before the workspace's records: the value counts from `day`, their first (S0 §21.5 rule 4). */
  | { code: 'NOT_RECORDED_BEFORE'; day: string }
  /** No value: the period ends before records begin on `day`, or none has begun yet (null). */
  | { code: 'NOT_RECORDED'; day: string | null }

export interface MetricChange {
  absolute: number
  /** Against the baseline; null when the baseline was zero. */
  relative: number | null
  direction: 'UP' | 'DOWN' | 'STEADY'
}

export interface MetricResult {
  metricId: OperationsMetricId
  unit: 'COUNT' | 'MINUTES'
  readiness: MetricReadiness
  /** Null whenever the readiness allows no value. */
  value: number | null
  baselineValue: number | null
  change: MetricChange | null
  betterWhen: 'LOWER' | 'HIGHER' | null
  notes: MetricNote[]
}

export interface OperationsSnapshot {
  period: {
    kind: PeriodKind
    first: string
    last: string
    baselineFirst: string
    baselineLast: string
    timezone: string
    timezoneKnown: boolean
  }
  dataHealth: { status: PipelineHealth; asOf: string | null; reason: BlockedReason | null }
  metrics: MetricResult[]
  /** What Changed (P2 §3.3): the metrics to talk about, in order. */
  whatChanged: OperationsMetricId[]
  /** The local day the workspace first recorded a booking or a visit; null while there is none (DECISIONS.md #34). */
  recordsFrom: string | null
  /**
   * Whether the period before was recorded from its first day; nothing is compared with it when it was not.
   * Null while ElfieCare cannot be read: whether records had begun is not known.
   */
  comparable: boolean | null
}

export const snapshotKeys = {
  operations: (workspaceId: string, selection: PeriodSelection) =>
    ['workspaces', workspaceId, 'snapshot', 'operations', selection.period, selection.from ?? '', selection.to ?? ''] as const,
}

/**
 * The figures a query keeps on screen while it loads: the last ones, but only
 * while the workspace stays the same. A new function for each workspace, since
 * TanStack Query keeps showing a placeholder it already shows for as long as the
 * function giving it is the same one: a period changed and then the workspace,
 * before the first answer came, would otherwise keep the first workspace's.
 */
export const keptWithin = (workspaceId: MaybeRefOrGetter<string>) =>
  computed(() => {
    const id = toValue(workspaceId)
    return <T>(previous: T | undefined, previousQuery?: { queryKey: readonly unknown[] }): T | undefined =>
      previousQuery?.queryKey[1] === id ? previous : undefined
  })

/** Numbers move as the day goes on: read again each minute while the page is open. */
const REFRESH_MS = 60_000

/**
 * The Institutional Snapshot's Operations metrics (P2 §3.2). The Overview and
 * Home's entry section both read this, so they cannot disagree.
 */
export const useOperationsSnapshot = (
  workspaceId: MaybeRefOrGetter<string>,
  selection: MaybeRefOrGetter<PeriodSelection>,
  enabled: MaybeRefOrGetter<boolean> = true,
) =>
  useQuery({
    queryKey: computed(() => snapshotKeys.operations(toValue(workspaceId), toValue(selection))),
    queryFn: async () => {
      const { period, from, to } = toValue(selection)
      const params = period === 'CUSTOM' ? { period, from, to } : { period }
      return unwrap(
        (await http.get<ApiEnvelope<OperationsSnapshot>>(`${workspacePath(toValue(workspaceId))}/snapshot/operations`, { params }))
          .data,
      )
    },
    enabled: computed(() => toValue(enabled)),
    // Changing the period keeps the last numbers on screen until the new ones arrive; changing the
    // workspace never does: one workspace's numbers are not shown under another's name.
    placeholderData: keptWithin(workspaceId),
    refetchInterval: REFRESH_MS,
    retry: (failures, error) => !(isApiError(error) && [400, 403, 404].includes(error.statusCode)) && failures < 1,
  })
